/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { Base } from "../../../framework/base";
import {
    TransportOrderListItemDto,
    TransportOrderState,
    TransportOrderInvoicingState,
    TransportOrderSaveDto,
} from "../../../models/transport/transportOrder";
import { Divider, IconButton, Tooltip } from "@mui/material";
import { TransportOrderStateIndicator, getDateTime } from "../components/transportUtils";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import { ConfirmationDialog } from "../../framework/confirmationDialog";

import { deleteTransportOrder } from "../../../services/transportOrderService";
import { showSuccessMessage } from "../../../models/store/storeActions";
import { showApiError } from "../../framework/formUtils";
import {
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
    GridRenderEditCellParams,
} from "@mui/x-data-grid-premium";
import { OrderStateSelect } from "./OrderFormComponents";
import { ICustomerListItem } from "../../../models/customer/customerListItem";
import {
    FieldError,
    MuiEditableDataGrid,
} from "../../framework/editableDataGrid/muiEditableDataGrid";
import { NotificationsStatus } from "../components/notificationsStatus";
import { VehicleListItemDto } from "../../../models/transport/vehicle";
import { OrderDetailsPanel } from "./orderDetailsPanel";
import { fetchTransportOrderTypes } from "../../../store/orderTypesSlice";
import { MuiDataGridDateTimePicker } from "../../framework/editableDataGrid/muiDataGridDateTimePicker";
import { MuiDataGridAutoComplete } from "../../framework/editableDataGrid/muiDataGridAutoComplete";
import { DataGridOrdersCustomerAutoComplete } from "../../framework/editableDataGrid/muiDataGridOrdersCustomerAutoComplete";
import {
    singleSelectSortComparator,
    stringOrNumberSortComparator,
} from "../../framework/dataGrid/sortComparator";
import MuiMenu from "../../framework/muiMenu";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { AttachFile, History, Smartphone } from "@mui/icons-material";
import { OrderEventLog } from "../components/orderEventLog";
import { OwnerParameters } from "../../../models/owner/ownerParameters";
import { PagedResult } from "../../../models/pagedResult/pagedResult";
import { OrderSource } from "../../../models/transport/orderSource";
import { Badge } from "@mui/material";

const VISIBLE_PRODUCTS = 5;

const formatTooltipText = (toolTipText: string | undefined) => {
    if (!toolTipText) {
        return "";
    }

    if (toolTipText.length > 200) {
        return `${toolTipText.slice(0, 200)}...`;
    }

    return toolTipText;
};

const formatProductTooltip = (products: any[]) => {
    if (!products || products.length === 0) return null;
    return (
        <React.Fragment>
            {products.map((product, index) => (
                <div key={product.id ?? `new-${index}`}>
                    {product.name} {product.unitPrice ? `(${Base.formatPrice(product.unitPrice)}€/${product.unitName})` : ""} - {product.quantity} {product.unitName}
                </div>
            ))}
        </React.Fragment>
    );
};

interface OrdersTableProps {
    orders: TransportOrderListItemDto[];
    setOrders: React.Dispatch<
        React.SetStateAction<TransportOrderListItemDto[]>
    >;
    fetchOrders: () => Promise<PagedResult<TransportOrderListItemDto>>;
    highlightedRowId: string | null;
    customers: ICustomerListItem[];
    vehicles: VehicleListItemDto[];
    handleSubmit: (
        values: TransportOrderListItemDto
    ) => Promise<TransportOrderListItemDto>;
    setIsEditMode: (isEdit: boolean) => void;
    setHighlightedRowId: (id: string) => void;
    currentPage: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    refreshCustomers?: () => void;
}

export const defaultTableOrderItem = {
    name: null,
    vehicleId: null,
    employeeId: null,
    scheduledStartDateTime: null,
    scheduledEndDateTime: null,
    state: TransportOrderState.OrderCreated,
    transportDetails: null,
    senderBusinessId: null,
    orderTypeId: null,
    formData: null,
    formValues: null,
    senderCustomerId: null,
    receiverBusinessId: null,
    receiverCustomerId: null,
    driverComments: null,
    billingCustomerId: null,
    fixedPrice: null,
    createdEmployeeId: null,
    products: [],
};

export const OrdersTable = ({
    orders,
    setOrders,
    highlightedRowId,
    fetchOrders,
    customers,
    vehicles,
    handleSubmit,
    setIsEditMode,
    setHighlightedRowId,
    currentPage,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    refreshCustomers,
}: OrdersTableProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const employees = useAppSelector((state) => state.employees.employees);
    const orderTypes = useAppSelector((state) => state.orderTypes.orderTypes);
    const [showDeleteModal, setShowDeleteModal] = useState<string | undefined>(
        undefined
    );
    const [editId, setEditId] = useState<string>();
    const [showEventLog, setShowEventLog] = useState<string | undefined>(
        undefined
    );
    const [expandedRow, setExpandedRow] = useState<{
        id: string;
        field: string;
    }>();
    const employeeOptions = useMemo(
        () =>
            employees?.map((e) => ({
                value: e.id,
                label: `${e.firstName} ${e.lastName}`,
                // Sort first by lastName, then by firstName
                sortLabel: `${e.lastName} ${e.firstName}`,
            })),
        [employees]
    );

    const customerOptions = useMemo(
        () =>
            customers?.map((e) => ({
                value: e.id,
                label: e.name,
            })),
        [customers]
    );

    const vehicleOptions = useMemo(
        () =>
            vehicles?.map((v) => ({
                value: v.id,
                label: `${v.brand} ${v.registerNumber}`,
                // Sort first by register number, then by brand
                sortLabel: `${v.registerNumber} ${v.brand}`,
            })),
        [vehicles]
    );

    // Convert our pagination to MUI DataGrid format
    const paginationModel = useMemo(() => ({
        page: currentPage - 1, // MUI uses 0-based indexing
        pageSize: pageSize
    }), [currentPage, pageSize]);

    const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
        onPageChange(model.page + 1); // Convert back to 1-based indexing
        onPageSizeChange(model.pageSize);
    }, [onPageChange, onPageSizeChange]);

    const getVehicleRegNumber = (id: string | null) => {
        if (id) {
            const vehicle = vehicles?.find(vehicle => vehicle.id === id);
            if (vehicle) {
                return vehicle?.registerNumber;
            }
        }
        return null;
    };

    const ownerParameters = useMemo(() => new OwnerParameters(), []);
    const notificationsEnabled = ownerParameters.getOwnerHasTransportNotificationsEnabled();

    useEffect(() => {
        setIsEditMode(!!editId);

        if (!editId) {
            setExpandedRow(null); // Close expanded row when exiting edit mode.
        }
    }, [editId]);

    useEffect(() => {
        dispatch(fetchTransportOrderTypes() as any);
    }, []);

    const orderTypeLinkText = (id: string | null): string => {
        if (id) {
            const orderType = orderTypes?.find((i) => i.orderTypeId === id);
            if (orderType) return orderType.title;
        }

        return t("transport.order.noOrderType");
    };

    const items = (row: TransportOrderListItemDto): Array<any> => {
        const menuItems = [];

        menuItems.push(
            {
                label: t("common.edit"),
                icon: <EditIcon color="primary" />,
                onClick: () => {
                    setExpandedRow({
                        id: row.id,
                        field: null,
                    });
                }
            }
        );
        menuItems.push(
            {
                label: t("transport.order.eventLog"),
                icon: <History color="primary" />,
                onClick: () => {
                    setHighlightedRowId(row.id);
                    setShowEventLog(row.id);
                }
            }
        );

        if (row.state === TransportOrderState.OrderCreated) {
            menuItems.push({ divider: true });
            menuItems.push(
                {
                    label: t("common.remove"),
                    icon: <DeleteIcon color="error" />,
                    onClick: () => {
                        setHighlightedRowId(row.id);
                        setShowDeleteModal(row.id);
                    }
                }
            );
        }

        return menuItems;
    };

    const gridDef: GridColDef<TransportOrderListItemDto>[] = useMemo(
        () => [
            {
                headerName: t("transport.order.state"),
                hideable: true,
                field: "state",
                editable: true,
                width: 25,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) => {
                    return (
                        <TransportOrderStateIndicator
                            state={params.value}
                            createdDatetime={params.row?.createdDateTime}
                            performedDatetime={
                                params.row
                                    ?.latestTransportTaskTypeChangedDateTime
                            }
                        />
                    );
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <OrderStateSelect {...params} />
                ),
            },
            {
                headerName: t("common.orderNumber"),
                hideable: true,
                field: "orderNumber",
                type: "string",
                flex: 1,
            },
            {
                headerName: t("transport.order.identifier"),
                hideable: true,
                field: "name",
                type: "string",
                editable: true,
                flex: 2,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                headerName: t("transport.order.sender"),
                hideable: true,
                field: "senderCustomerId",
                type: "singleSelect",
                valueOptions: customerOptions,
                flex: 2,
                editable: true,
                renderCell: (params: GridRenderCellParams<TransportOrderListItemDto>) => params.row.senderName ?? "",
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridOrdersCustomerAutoComplete {...params} refreshCustomers={refreshCustomers} />
                ),
                sortComparator: (v1: string, v2: string, param1, param2) =>
                    singleSelectSortComparator(
                        v1,
                        v2,
                        param1,
                        param2,
                        customerOptions
                    ),
            },
            {
                headerName: t("transport.order.receiver"),
                hideable: true,
                field: "receiverCustomerId",
                type: "singleSelect",
                valueOptions: customerOptions,
                editable: true,
                flex: 2,
                renderCell: (params: GridRenderCellParams<TransportOrderListItemDto>) => params.row.receiverName ?? "",
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridOrdersCustomerAutoComplete {...params} refreshCustomers={refreshCustomers} />
                ),
                sortComparator: (v1: string, v2: string, param1, param2) =>
                    singleSelectSortComparator(
                        v1,
                        v2,
                        param1,
                        param2,
                        customerOptions
                    ),
            },
            {
                headerName: t("transport.order.billingCustomer"),
                hideable: false,
                field: "billingCustomerId",
                type: "singleSelect",
                valueOptions: customerOptions,
                editable: true,
                flex: 2,
                renderCell: (params: GridRenderCellParams<TransportOrderListItemDto>) => params.row.billingCustomerName ?? "",
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridOrdersCustomerAutoComplete {...params} refreshCustomers={refreshCustomers} />
                ),
                sortComparator: (v1: string, v2: string, param1, param2) =>
                    singleSelectSortComparator(
                        v1,
                        v2,
                        param1,
                        param2,
                        customerOptions
                    ),
            },
            {
                headerName: t("common.price"),
                hideable: true,
                field: "fixedPrice",
                type: "number",
                editable: true,
                width: 100,
                sortComparator: stringOrNumberSortComparator,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) => {
                    if (params.row.fixedPrice !== null) {
                        return (
                            <div>
                                {params.row.fixedPrice.toLocaleFixed(2) + " €"}
                            </div>
                        );
                    }
                },
            },
            {
                headerName: t("vehicle.vehicle"),
                hideable: true,
                field: "vehicleId",
                type: "singleSelect",
                valueOptions: vehicleOptions,
                editable: true,
                width: 100,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) => {
                    return (
                        <Tooltip title={params.row.vehicleName}>
                            <div>
                                {getVehicleRegNumber(params.row.vehicleId)}
                            </div>
                        </Tooltip>
                    );
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <MuiDataGridAutoComplete {...params} />
                ),
                sortComparator: (v1: string, v2: string, param1, param2) =>
                    singleSelectSortComparator(
                        v1,
                        v2,
                        param1,
                        param2,
                        vehicleOptions
                    ),
            },
            {
                headerName: t("transport.driver"),
                hideable: true,
                field: "employeeId",
                type: "singleSelect",
                valueOptions: employeeOptions,
                editable: true,
                flex: 2,
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <MuiDataGridAutoComplete {...params} />
                ),
                sortComparator: (v1: string, v2: string, param1, param2) =>
                    singleSelectSortComparator(
                        v1,
                        v2,
                        param1,
                        param2,
                        employeeOptions
                    ),
            },
            {
                headerName: t("transport.order.startTime"),
                hideable: true,
                field: "scheduledStartDateTime",
                editable: true,
                width: 135,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) => getDateTime(params.row?.scheduledStartDateTime),
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <MuiDataGridDateTimePicker {...params} />
                ),
                sortComparator: stringOrNumberSortComparator,
            },
            {
                headerName: t("transport.order.endTime"),
                hideable: true,
                field: "scheduledEndDateTime",
                editable: true,
                width: 135,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) => getDateTime(params.row?.scheduledEndDateTime),
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <MuiDataGridDateTimePicker {...params} />
                ),
                sortComparator: stringOrNumberSortComparator,
            },
            {
                headerName: t("common.created"),
                hideable: true,
                field: "createdDateTime",
                width: 125,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) => {
                    return (
                        <div>
                            {params.row?.source === OrderSource.Mobile && (
                                <IconButton
                                    disableRipple
                                    sx={{ paddingLeft: 0 }}
                                >
                                    <Smartphone />
                                </IconButton>
                            )}
                            {getDateTime(params.row?.createdDateTime)}
                        </div>
                    );
                },
            },
            {
                headerName: t("transport.order.creator"),
                hideable: true,
                field: "createdEmployeeId",
                type: "singleSelect",
                flex: 1,
                valueOptions: employeeOptions,
                editable: false,
                sortComparator: (v1: string, v2: string, param1, param2) =>
                    singleSelectSortComparator(
                        v1,
                        v2,
                        param1,
                        param2,
                        employeeOptions
                    ),
            },
            {
                headerName: t("transport.products"),
                hideable: true,
                field: "products",
                flex: 1,
                sortComparator: (v1, v2, param1, param2) =>
                    stringOrNumberSortComparator(
                        v1[0]?.name as string,
                        v2[0]?.name as string,
                        param1,
                        param2
                    ),
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) => {
                    const isDisabled = !!editId && editId !== params.id;
                    return (
                        <Tooltip title={formatProductTooltip(params.row.products)}>
                            <div
                                onClick={
                                    !isDisabled
                                        ? () => {
                                            setExpandedRow({
                                                id: params.row.id,
                                                field: "products",
                                            });
                                        }
                                        : undefined
                                }
                                style={
                                    params.row.products.length > VISIBLE_PRODUCTS
                                        ? {
                                            maxHeight: "100px",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                        }
                                        : {}
                                }
                            >
                                <span
                                    style={{
                                        color: "dodgerblue",
                                        cursor: !isDisabled ? "pointer" : "initial",
                                    }}
                                >
                                    {params.row.products.length === 0 && (
                                        <div style={{ color: "gray" }}>
                                            {t("transport.order.noProducts")}
                                        </div>
                                    )}
                                    {params.row.products.length > 0 &&
                                        params.row.products.map((product) => {
                                            return (
                                                <div
                                                    key={product.id ?? "new"}
                                                    style={{
                                                        minWidth: "100px",
                                                        fontSize: "small",
                                                    }}
                                                >
                                                    {product.name}{" "}
                                                    {product.quantity > 1
                                                        ? `${product.quantity} ${product.unitName}`
                                                        : ""}
                                                </div>
                                            );
                                        })}
                                </span>
                            </div>
                        </Tooltip>
                    );
                },
            },
            {
                headerName: t("common.type"),
                hideable: true,
                field: "formTitle",
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) => {
                    const isDisabled = !!editId && editId !== params.id;

                    return (
                        <div
                            onClick={
                                !isDisabled
                                    ? () => {
                                        setExpandedRow({
                                            id: params.row.id,
                                            field: "orderTypeId",
                                        });
                                    }
                                    : undefined
                            }
                        >
                            <span
                                style={{
                                    color:
                                        isDisabled || !params.row.orderTypeId
                                            ? "gray"
                                            : "dodgerblue",
                                    cursor:
                                        isDisabled || !params.row.orderTypeId
                                            ? "initial"
                                            : "pointer",
                                }}
                            >
                                {orderTypeLinkText(params.row.orderTypeId)}
                            </span>
                        </div>
                    );
                },
            },
            {
                headerName: "",
                hideable: false,
                field: "actions",
                width: notificationsEnabled ? 150 : 80,
                type: "actions",
                renderCell: (
                    params: GridRenderCellParams<TransportOrderListItemDto>
                ) =>
                    params.row.invoicingState !==
                        TransportOrderInvoicingState.InvoicedInternally &&
                    params.row.invoicingState !==
                        TransportOrderInvoicingState.InvoicedExternally ? (
                            <>
                                <div className="d-flex align-items-center justify-content-flex-start">
                                    <IconButton
                                        disableRipple
                                        disabled={
                                        !!editId && editId !== params.id
                                    }
                                        onClick={() =>
                                            setExpandedRow({
                                                id: params.row.id,
                                                field: "attachments",
                                            })
                                    }
                                        sx={{ p: 0.5 }}
                                        style={{
                                            cursor:
                                            !!editId &&
                                            editId !== params.id
                                                ? "not-allowed"
                                                : "pointer",
                                        }}
                                    >
                                        {!editId && params.row.attachments?.length > 0 ? (
                                            <Badge
                                                overlap="circular"
                                                badgeContent={params.row.attachments?.length}
                                                color="primary"
                                            >
                                                <AttachFile color="primary" />
                                            </Badge>
                                        ) : (
                                            <AttachFile color="gray" />
                                        )}
                                    </IconButton>
                                    <Tooltip
                                        title={
                                        params.row.transportDetails ||
                                        params.row.driverComments ? (
                                            <React.Fragment>
                                                {params.row.transportDetails &&
                                                    t(
                                                        formatTooltipText(
                                                            params.row
                                                                .transportDetails
                                                        )
                                                    )}
                                                {params.row.transportDetails &&
                                                    params.row
                                                        .driverComments && (
                                                        <Divider
                                                            sx={{
                                                                borderColor:
                                                                    "white !important",
                                                                mt: 0.5,
                                                                mb: 0.5,
                                                            }}
                                                        />
                                                )}
                                                {params.row.driverComments &&
                                                    t(
                                                        formatTooltipText(
                                                            params.row
                                                                .driverComments
                                                        )
                                                    )}
                                            </React.Fragment>
                                            ) : null
                                    }
                                    >
                                        <span>
                                            <IconButton
                                                disableRipple
                                                disabled={
                                                !!editId && editId !== params.id
                                            }
                                                onClick={() =>
                                                    setExpandedRow({
                                                        id: params.row.id,
                                                        field: "transportDetails",
                                                    })
                                            }
                                                sx={{ p: 0.5 }}
                                                style={{
                                                    cursor:
                                                    !!editId &&
                                                    editId !== params.id
                                                        ? "not-allowed"
                                                        : "pointer",
                                                }}
                                            >
                                                {!editId &&
                                            (params.row.transportDetails ||
                                                params.row.driverComments) ? (
                                                    <InfoIcon color="primary" />
                                                    ) : (
                                                        <InfoIcon color="gray" />
                                                    )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {notificationsEnabled && (
                                        <>
                                            <IconButton
                                                disableRipple
                                                sx={{ p: 0.5 }}
                                                disabled={!!editId}
                                            >
                                                <NotificationsStatus
                                                    row={params.row}
                                                    notificationContext="toDriver"
                                                />
                                            </IconButton>
                                            <IconButton
                                                disableRipple
                                                sx={{ p: 0.5 }}
                                                disabled={!!editId}
                                            >
                                                <NotificationsStatus
                                                    row={params.row}
                                                    notificationContext="fromDriver"
                                                />
                                            </IconButton>
                                        </>
                                    )}
                                    <MuiMenu
                                        label={
                                            <MoreHorizIcon
                                                color={editId ? "gray" : "primary"}
                                            />
                                        }
                                        items={items(params.row)}
                                        disabled={!!editId}
                                    />
                                </div>
                            </>
                        ) : null,
            },
        ],
        [editId, customerOptions, vehicleOptions, employeeOptions, orderTypes]
    );

    const handleAddRow = (): TransportOrderSaveDto => {
        return { ...defaultTableOrderItem, products: [] };
    };

    const validateRow = useCallback((row: TransportOrderSaveDto) => {
        const errors: FieldError[] = [];

        if (!row.billingCustomerId) {
            errors.push({ field: "billingCustomerId", error: t("validation.fieldIsRequired", {
                field: t("transport.order.billingCustomer"),
            }) });
        }

        if (!!row["products_errors"]) {
            row["products_errors"]
                .filter(Boolean)
                .flatMap((e) =>
                    Object.values(e).map(
                        (v) =>
                            ({
                                field: "products",
                                error: v,
                            } as FieldError)
                    )
                )
                .forEach((e: FieldError) => {
                    errors.push(e);
                });
        }

        if (!!row["formValues_errors"]) {
            row["formValues_errors"]
                .filter(Boolean)
                .map(
                    (e) =>
                        ({
                            field: "formValues",
                            error: e.message,
                        } as FieldError)
                )
                .forEach((e: FieldError) => {
                    errors.push(e);
                });
        }

        return errors;
    }, []);

    const getDetailsComponent = useCallback(
        (params) => (
            <OrderDetailsPanel
                {...params}
                onClose={() => setExpandedRow(null)}
                field={expandedRow?.field}
            />
        ),
        [setExpandedRow, expandedRow]
    );

    return (
        <>
            <MuiEditableDataGrid<Partial<TransportOrderSaveDto>>
                editId={editId}
                persistStateKey="EditableOrdersTableState"
                setEditId={setEditId}
                columns={gridDef}
                rows={orders}
                setRows={setOrders}
                handleAddRow={handleAddRow}
                handleSubmit={handleSubmit}
                validateRow={validateRow}
                addRowText={t("transport.order.newOrder")}
                rowSelectionModel={[highlightedRowId, expandedRow?.id].filter(Boolean)}
                isCellEditable={(params) =>
                    // Disable state edit for new rows
                    params.id !== "new" || params.field !== "state"
                }
                detailsComponent={getDetailsComponent}
                detailPanelExpandedRowIds={[expandedRow?.id].filter(Boolean)}
                highlightedRowId={highlightedRowId}
                setHighlightedRowId={setHighlightedRowId}
                pagination={true}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                rowCount={totalCount}
                pageSizeOptions={[25, 50, 100]}
                sortingMode="client"
                autosizeColumnsByName={["fixedPrice", "vehicleId"]}
            />
            {showDeleteModal && (
                <ConfirmationDialog
                    title={""}
                    text={t("transport.order.deleteConfirmation")}
                    show={true}
                    onYes={async() => {
                        try {
                            await deleteTransportOrder(showDeleteModal);
                            dispatch(
                                showSuccessMessage(
                                    t("transport.order.deleteSuccess")
                                )
                            );
                            setHighlightedRowId(null);
                            await fetchOrders();
                        } catch (error) {
                            showApiError(error);
                        } finally {
                            setShowDeleteModal(undefined);
                        }
                    }}
                    onNo={() => setShowDeleteModal(undefined)}
                    onCancel={() => setShowDeleteModal(undefined)}
                />
            )}
            {showEventLog && (
                <OrderEventLog
                    transportOrderId={showEventLog}
                    show={true}
                    onClose={() => setShowEventLog(undefined)}
                />
            )}
        </>
    );
};
